import { useToastController } from "@tamagui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  type ComponentType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ActiveMedications } from "@/ai-doctor/ActiveMedications";
import { AIDoctorConsentForm } from "@/ai-doctor/AIDoctorConsentForm";
import { RenewalsChosenStep } from "@/ai-doctor/RenewalsChosenStep";
import { RFEScreen } from "@/ai-doctor/RFEScreen";
import { RxHistoryFetch } from "@/ai-doctor/RxHistoryFetch";
import { SurescriptsConsentScreen } from "@/ai-doctor/SurescriptsConsentScreen";
import { VitalsScreen } from "@/ai-doctor/VitalsScreen";
import {
  getGetClinicRequestQueryKey,
  useAdvanceUserProgressionToNextStep,
  useArchiveUserProgression,
  useGoBackInUserProgression,
  useGoToPaymentSelection,
} from "@/api/generated";
import {
  PartnerMatchingRequestMetadataValue,
  type ProductOption,
  ProgressionStep,
  UserProgressionStateModel,
  UserProgressionStatus,
} from "@/api/generated/models";
import type { AdvanceProgressionRequest } from "@/api/generated/models/AdvanceProgressionRequest";
import { useGetClinicStatusForPatient } from "@/api/queries/clinicStatus";
import { LayoutContext } from "@/components/LayoutProvider";
import { AccessIneligibleScreen } from "@/conversation/sidebar/access/AccessIneligibleScreen";
import { CareTeamCollectionFlow } from "@/conversation/sidebar/access/CareTeamCollectionFlow";
import { CMSConsentScreen } from "@/conversation/sidebar/access/CMSConsentScreen";
import { CMSEligibilityScreen } from "@/conversation/sidebar/access/CMSEligibilityScreen";
import StripeRedirectScreen from "@/conversation/sidebar/Payment/StripeRedirectScreen";
import VisitOptionsScreen from "@/conversation/sidebar/Payment/VisitOptionsScreen";
import QuestionnaireProgressionStep from "@/conversation/sidebar/UserProgression/QuestionnaireProgressionStep";
import { useKeepConversationsOpen } from "@/hooks/useGroupMessages";
import { captureException } from "@/sentry";
import { useActiveProgressionStore } from "@/store/activeProgression";
import { isClinicRequestExpiredError } from "@/utils/clinicRequestError";
import VideoVisitExplainer from "../../video/VideoVisitExplainer";
import AsyncVisit from "../AsyncVisit";
import ClinicRedirect from "../ClinicRedirect";
import CuraiConsentScreen from "../CuraiConsentScreen";
import FirstVisitPromo from "../FirstVisitPromo";
import PharmacySelect from "../PharmacySelect";
import { ProfileFormSidebar } from "../ProfileFormSidebar";
import { RemediationScreenWithDialog } from "../RemediationScreen";
import SidebarSignin from "../SidebarSignin";
import StateValidation from "../StateValidation";
import Subjective from "../Subjective";
import { ActiveVisitScreen } from "./ActiveVisitScreen";
import ConfirmIdVerification from "./ConfirmIdVerification";
import StartDatabaseIdVerification from "./StartDatabaseIdVerification";
import UserProgressionContext from "./UserProgressionContext";
import UserProgressionScreenWrapper, {
  type DialogVariant,
} from "./UserProgressionScreenWrapper";

type Variant = "subclinical" | "clinical";

type StepConfig = {
  component: ComponentType<any>;
  variant: Variant;
  dataTrackingIdPrefix: string;
  dialogVariant?: DialogVariant;
  handlesDialogShell?: boolean;
  /** Hide the back button entirely on this step (e.g. terminal/transitional screens) */
  hideBackButton?: boolean;
  /** Desktop modal width override for this step (defaults to the standard width). */
  modalWidth?: number;
};

// Per-flow variant overrides for steps whose default doesn't fit every flow.
// E.g. ID verification is clinical by default, but subclinical in
// partner_post_registration where the whole journey is subclinical.
const VARIANT_OVERRIDES: Partial<
  Record<string, Partial<Record<ProgressionStep, Variant>>>
> = {
  partner_post_registration: {
    [ProgressionStep.start_database_id_verification]: "subclinical",
    [ProgressionStep.confirm_id_verification_status]: "subclinical",
  },
};

const buildStepConfigs = (
  flowName: string,
): Record<ProgressionStep, StepConfig> => {
  const overrides = VARIANT_OVERRIDES[flowName] ?? {};
  const withOverride = (
    step: ProgressionStep,
    config: StepConfig,
  ): StepConfig =>
    overrides[step]
      ? { ...config, variant: overrides[step] as Variant }
      : config;
  const configs: Record<ProgressionStep, StepConfig> = {
    [ProgressionStep.affiliate_welcome]: {
      component: () => <></>, // DEPRECATED AS A PROGRESSION STEP
      variant: "subclinical",
      dataTrackingIdPrefix: "affiliate-welcome",
      hideBackButton: true,
    },
    [ProgressionStep.explainer]: {
      component: FirstVisitPromo,
      variant: "subclinical",
      dataTrackingIdPrefix: "first-visit-promo",
    },
    [ProgressionStep.authentication]: {
      component: SidebarSignin,
      variant: "subclinical",
      dataTrackingIdPrefix: "signin-sidebar",
      modalWidth: 400,
    },
    [ProgressionStep.profile_completion]: {
      component: ProfileFormSidebar,
      variant: "subclinical",
      dataTrackingIdPrefix: "profile-builder",
    },
    [ProgressionStep.subjective_shareback]: {
      component: Subjective,
      variant: "subclinical",
      dataTrackingIdPrefix: "subjective",
    },
    [ProgressionStep.video_visit_explainer]: {
      component: VideoVisitExplainer,
      variant: "clinical",
      dataTrackingIdPrefix: "video-visit-explainer",
    },
    [ProgressionStep.state_validation]: {
      component: StateValidation,
      variant: "clinical",
      dataTrackingIdPrefix: "state-validation",
    },
    [ProgressionStep.start_database_id_verification]: {
      component: StartDatabaseIdVerification,
      variant: "clinical",
      dataTrackingIdPrefix: "start-database-id-verification",
      hideBackButton: true,
    },
    [ProgressionStep.confirm_id_verification_status]: {
      component: ConfirmIdVerification,
      variant: "clinical",
      dataTrackingIdPrefix: "confirm-id-verification",
    },
    [ProgressionStep.consent]: {
      component: CuraiConsentScreen,
      variant: "clinical",
      dataTrackingIdPrefix: "curai-consent",
    },
    [ProgressionStep.payment_selection]: {
      component: VisitOptionsScreen,
      variant: "subclinical",
      dataTrackingIdPrefix: "product-options",
      hideBackButton: true,
    },
    [ProgressionStep.stripe_redirect]: {
      component: StripeRedirectScreen,
      variant: "subclinical",
      dataTrackingIdPrefix: "stripe-redirect",
    },
    [ProgressionStep.visit_selection]: {
      component: VisitOptionsScreen,
      variant: "subclinical",
      dataTrackingIdPrefix: "visit-options",
      hideBackButton: true,
    },
    [ProgressionStep.pharmacy_selection]: {
      component: PharmacySelect,
      variant: "clinical",
      dataTrackingIdPrefix: "pharmacy-select",
    },
    [ProgressionStep.clinic_redirect]: {
      component: ClinicRedirect,
      variant: "clinical",
      dataTrackingIdPrefix: "clinic-redirect",
      hideBackButton: true,
    },
    [ProgressionStep.async_visit]: {
      component: AsyncVisit,
      variant: "clinical",
      dataTrackingIdPrefix: "async-visit",
      hideBackButton: true,
    },
    [ProgressionStep.partner_remediation]: {
      component: RemediationScreenWithDialog,
      variant: "subclinical",
      dataTrackingIdPrefix: "partner-remediation",
      handlesDialogShell: true,
      hideBackButton: true,
    },
    [ProgressionStep.renewals_chosen]: {
      component: RenewalsChosenStep,
      variant: "clinical",
      dataTrackingIdPrefix: "renewals-chosen",
      hideBackButton: true,
    },
    [ProgressionStep.rfe]: {
      component: RFEScreen,
      variant: "clinical",
      dataTrackingIdPrefix: "rfe",
      hideBackButton: true,
    },
    [ProgressionStep.ai_doctor_consent]: {
      component: AIDoctorConsentForm,
      variant: "clinical",
      dataTrackingIdPrefix: "ai-doctor-consent",
    },
    [ProgressionStep.rx_history_fetch]: {
      component: RxHistoryFetch,
      variant: "clinical",
      dataTrackingIdPrefix: "rx-history-fetch",
      hideBackButton: true,
    },
    [ProgressionStep.active_medications]: {
      component: ActiveMedications,
      variant: "clinical",
      dataTrackingIdPrefix: "active-medications",
      hideBackButton: true,
    },
    [ProgressionStep.vitals]: {
      component: VitalsScreen,
      variant: "subclinical",
      dataTrackingIdPrefix: "vitals",
    },
    [ProgressionStep.surescripts_consent]: {
      component: SurescriptsConsentScreen,
      variant: "clinical",
      dataTrackingIdPrefix: "surescripts-consent",
    },
    [ProgressionStep.cms_eligibility]: {
      component: CMSEligibilityScreen,
      variant: "subclinical",
      dataTrackingIdPrefix: "cms-eligibility",
      hideBackButton: true,
    },
    [ProgressionStep.access_ineligible]: {
      component: AccessIneligibleScreen,
      variant: "subclinical",
      dataTrackingIdPrefix: "access-ineligible",
      hideBackButton: true,
    },
    [ProgressionStep.cms_consent]: {
      component: CMSConsentScreen,
      variant: "subclinical",
      dataTrackingIdPrefix: "cms-consent",
    },
    [ProgressionStep.questionnaire]: {
      component: QuestionnaireProgressionStep,
      variant: "clinical",
      dataTrackingIdPrefix: "questionnaire",
    },
    [ProgressionStep.care_team_collection]: {
      component: CareTeamCollectionFlow,
      variant: "clinical",
      dataTrackingIdPrefix: "care-team-collection",
    },
  };
  return Object.fromEntries(
    Object.entries(configs).map(([step, config]) => [
      step,
      withOverride(step as ProgressionStep, config),
    ]),
  ) as Record<ProgressionStep, StepConfig>;
};

interface UserProgressionFlowProps {
  initialProgression: UserProgressionStateModel;
  originClinicRequestId?: string;
  handleComplete?: () => Promise<unknown>;
  /** Called when the progression finishes, before `onClose`, which also fires on an early exit. */
  onComplete?: () => void;
  onClose?: () => void;
  hideCloseButton?: boolean;
}

const UserProgressionFlow = ({
  initialProgression,
  originClinicRequestId,
  handleComplete,
  onComplete,
  onClose,
  hideCloseButton,
}: UserProgressionFlowProps) => {
  const [progression, setProgression] =
    useState<UserProgressionStateModel>(initialProgression);
  const conversationId = progression.conversation_id ?? undefined;
  const clinicRequestId = progression.clinic_request_id ?? undefined;

  const shouldCheckForOngoingVisit =
    progression.current_step !== ProgressionStep.clinic_redirect &&
    progression.current_step !== ProgressionStep.async_visit &&
    (progression.steps.includes(ProgressionStep.clinic_redirect) ||
      progression.steps.includes(ProgressionStep.async_visit));

  const {
    data: clinicStatus,
    isFetchedAfterMount: hasFreshClinicStatus,
    isError: isClinicStatusError,
    refetch: refetchClinicStatus,
  } = useGetClinicStatusForPatient({
    enabled: shouldCheckForOngoingVisit,
    suppressErrorToast: true,
  });

  const STEP_CONFIGS = useMemo(
    () => buildStepConfigs(progression.flow_name),
    [progression.flow_name],
  );

  // When modal options change between steps, we set them, and clear them when the flow unmounts.
  const { setModalOptions } = useContext(LayoutContext);
  const activeStepModalWidth = progression.current_step
    ? STEP_CONFIGS[progression.current_step]?.modalWidth
    : undefined;
  useEffect(() => {
    setModalOptions((prev) => ({
      ...prev,
      width: activeStepModalWidth,
      dismissOnOverlayPress: false,
    }));
    return () =>
      setModalOptions((prev) => ({
        ...prev,
        width: undefined,
        dismissOnOverlayPress: undefined,
      }));
  }, [activeStepModalWidth, setModalOptions]);

  // Tracks screen-internal back button state (e.g. SignIn OTP → phone, ProfileForm page 2 → 1)
  const [showScreenBackButton, setShowScreenBackButton] = useState(false);
  const [suppressPreviousStepBackButton, setSuppressPreviousStepBackButton] =
    useState(false);
  const screenBackButtonOnPress = useRef<(() => void) | undefined>(undefined);

  const { show: showToast } = useToastController();
  const keepConversationsOpen = useKeepConversationsOpen();
  const requestExpiredToast = keepConversationsOpen
    ? "This request expired. Tell me what you need and I can set up a new visit."
    : "This request expired. Please start a new chat to continue.";

  const handleClinicRequestExpired = useCallback(() => {
    showToast(requestExpiredToast);
    onClose?.();
  }, [onClose, requestExpiredToast, showToast]);

  const { mutateAsync: advanceToNextStep, isPending: isAdvancing } =
    useAdvanceUserProgressionToNextStep({
      mutation: { meta: { suppressErrorToast: true } },
    });
  const { mutate: goBack, isPending: isGoingBack } = useGoBackInUserProgression(
    {
      mutation: {
        meta: { suppressErrorToast: true },
        onSuccess: (updated) => setProgression(updated),
        onError: (error) => {
          // backend returns 400 if there are no backtrackable steps, but we don't
          // want to just do nothing and show an error toast, so suppress the error toast
          // and close the sidebar
          if (error instanceof AxiosError && error.response?.status === 400) {
            onClose?.();
          } else {
            showToast("Something went wrong. Please try again.");
          }
        },
      },
    },
  );
  const { mutate: returnToPaymentSelection } = useGoToPaymentSelection({
    mutation: {
      meta: { suppressErrorToast: true },
      onSuccess: (updated) => setProgression(updated),
      onError: () => showToast("Something went wrong. Please try again."),
    },
  });
  const queryClient = useQueryClient();
  const { mutateAsync: archiveProgression } = useArchiveUserProgression({
    mutation: { meta: { suppressErrorToast: true } },
  });

  // Fire once: callers pass inline callbacks, and closing re-renders the modal while it animates out.
  const hasCompletedRef = useRef(false);
  useEffect(() => {
    if (
      progression.status !== UserProgressionStatus.completed ||
      hasCompletedRef.current
    )
      return;
    hasCompletedRef.current = true;
    onComplete?.();
    onClose?.();
  }, [progression.status, onComplete, onClose]);

  // maintain a reference to the active progression in the store, this can be
  // useful to other code that may wish to start a user progression to know if the
  // user is already progressing through one
  const setActiveProgression = useActiveProgressionStore(
    (s) => s.setActiveProgression,
  );
  useEffect(() => {
    setActiveProgression(progression);
    return () => setActiveProgression(null);
  }, [progression, setActiveProgression]);

  // Reset screen-internal back button when the progression step changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-run when current_step changes
  useEffect(() => {
    screenBackButtonOnPress.current = undefined;
    setShowScreenBackButton(false);
    setSuppressPreviousStepBackButton(false);
  }, [progression.current_step]);

  const handleNextStep = useCallback(
    async (args?: { metadata?: AdvanceProgressionRequest["metadata"] }) => {
      if (!progression.current_step) return false;
      try {
        if (shouldCheckForOngoingVisit) {
          const { data: refreshedClinicStatus } = await refetchClinicStatus({
            throwOnError: true,
          });
          if (refreshedClinicStatus?.hasOngoingClinicVisit) return false;
        }
        const updated = await advanceToNextStep({
          progressionId: progression.id,
          data: {
            step_id: progression.current_step,
            metadata: args?.metadata,
          },
        });
        setProgression(updated);
        return true;
      } catch (error) {
        if (isClinicRequestExpiredError(error)) {
          handleClinicRequestExpired();
        } else {
          showToast("Something went wrong. Please try again.");
        }
        return false;
      }
    },
    [
      progression.id,
      progression.current_step,
      shouldCheckForOngoingVisit,
      refetchClinicStatus,
      advanceToNextStep,
      handleClinicRequestExpired,
      showToast,
    ],
  );

  const handleReturnToPaymentSelection = useCallback(() => {
    returnToPaymentSelection({ progressionId: progression.id });
  }, [progression.id, returnToPaymentSelection]);

  // A failed archive must not strand the user on a step they're already done with.
  const handleArchive = useCallback(async () => {
    try {
      await archiveProgression({ progressionId: progression.id });
      if (conversationId && clinicRequestId) {
        await queryClient.invalidateQueries({
          queryKey: getGetClinicRequestQueryKey(
            conversationId,
            clinicRequestId,
          ),
        });
      }
    } catch (error) {
      captureException(error, {
        extra: { context: "UserProgressionFlow.handleArchive" },
      });
    }
    onClose?.();
  }, [
    archiveProgression,
    progression.id,
    onClose,
    conversationId,
    clinicRequestId,
    queryClient,
  ]);

  const handlePreviousStep = useCallback(() => {
    if (!progression.current_step) return;

    goBack({
      progressionId: progression.id,
      data: { step_id: progression.current_step },
    });
  }, [progression.id, progression.current_step, goBack]);

  // Callback for screens with internal sub-step navigation
  const handleBackButtonChange = useCallback(
    (show: boolean, onPress?: (() => void) | null) => {
      // A null handler explicitly hides the previous-step back button; undefined uses it.
      screenBackButtonOnPress.current = onPress ?? undefined;
      setShowScreenBackButton(show);
      setSuppressPreviousStepBackButton(!show && onPress === null);
    },
    [],
  );

  // Kept stable so screens that wire these into effects don't re-run on every
  // render of this component.
  const handlePaymentBlocked = useCallback(
    (updated: UserProgressionStateModel) => {
      showToast("Please choose a payment option to continue.");
      setProgression(updated);
    },
    [showToast],
  );
  const handleVisitBlocked = useCallback(
    (updated: UserProgressionStateModel) => {
      showToast("Please choose a payment option to start your visit.");
      setProgression(updated);
    },
    [showToast],
  );

  const currentStep = progression.current_step;
  if (!currentStep) return null;

  if (
    shouldCheckForOngoingVisit &&
    hasFreshClinicStatus &&
    !isClinicStatusError &&
    clinicStatus?.hasOngoingClinicVisit
  ) {
    return (
      <UserProgressionContext.Provider value={{ progression }}>
        <UserProgressionScreenWrapper
          variant="subclinical"
          dataTrackingIdPrefix="active-visit"
          onClose={onClose}
          hideCloseButton={hideCloseButton}
          showBackButton={false}
        >
          <ActiveVisitScreen onAcknowledge={() => onClose?.()} />
        </UserProgressionScreenWrapper>
      </UserProgressionContext.Provider>
    );
  }

  const stepConfig = STEP_CONFIGS[currentStep];
  if (!stepConfig) return null;
  const StepComponent = stepConfig.component;

  // Determine back button visibility and handler:
  // 1. If the step config hides the back button, never show it
  // 2. If the screen has its own internal back (sign in), use that
  // 3. Otherwise, show back if there are completed steps to go back to
  const hasCompletedSteps = (progression.completed_steps?.length ?? 0) > 0;
  const showBackButton =
    !stepConfig.hideBackButton &&
    (showScreenBackButton ||
      (!suppressPreviousStepBackButton && hasCompletedSteps));
  const onBackPress = showScreenBackButton
    ? screenBackButtonOnPress.current
    : !suppressPreviousStepBackButton && hasCompletedSteps
      ? handlePreviousStep
      : undefined;

  // Steps that need additional props beyond onContinue
  const extraProps: Record<string, unknown> = {};
  // Pass onBackButtonChange to screens that manage their own internal back state
  if (
    currentStep === ProgressionStep.authentication ||
    currentStep === ProgressionStep.profile_completion ||
    currentStep === ProgressionStep.care_team_collection
  ) {
    extraProps.onBackButtonChange = handleBackButtonChange;
  }
  if (
    currentStep === ProgressionStep.authentication ||
    currentStep === ProgressionStep.subjective_shareback
  ) {
    // auth: needed so complete-signin/complete-onboarding can migrate anonymous data
    // subjective: needed to fetch and update the subjective shareback
    extraProps.conversationId = conversationId;
    if (currentStep === ProgressionStep.subjective_shareback) {
      extraProps.clinicRequestId = clinicRequestId;
      // An async→live escalation ends this progression, so the step closes the flow itself.
      extraProps.onClose = onClose;
    }
  }
  if (currentStep === ProgressionStep.stripe_redirect) {
    extraProps.onPaymentBlocked = handlePaymentBlocked;
    extraProps.onClinicRequestExpired = handleClinicRequestExpired;
  }
  if (currentStep === ProgressionStep.state_validation) {
    extraProps.showConsentDisclaimer = !progression.steps.includes(
      ProgressionStep.consent,
    );
  }
  if (
    currentStep === ProgressionStep.payment_selection ||
    currentStep === ProgressionStep.visit_selection
  ) {
    extraProps.chosenProductOption = progression.metadata
      ?.payment_product_option as ProductOption | undefined;
  }
  if (currentStep === ProgressionStep.payment_selection) {
    extraProps.onActivateMembership = () =>
      handleNextStep({
        metadata: PartnerMatchingRequestMetadataValue,
      });
  }
  if (currentStep === ProgressionStep.care_team_collection) {
    extraProps.isPending = isAdvancing;
  }
  if (
    currentStep === ProgressionStep.partner_remediation &&
    progression.steps.includes(ProgressionStep.payment_selection)
  ) {
    extraProps.onReturnToPaymentSelection = handleReturnToPaymentSelection;
  }
  if (currentStep === ProgressionStep.video_visit_explainer) {
    extraProps.showAvRequiredForChatContent =
      progression.metadata?.visit_type_preference === "chat" &&
      progression.metadata?.is_av_call_required === true;
    extraProps.onClose = onClose;
  }
  if (currentStep === ProgressionStep.clinic_redirect) {
    extraProps.originClinicRequestId = originClinicRequestId;
    extraProps.clinicRequestId = clinicRequestId;
    extraProps.conversationId = conversationId;
    // A refused visit (402 entitlement / 409 selection mismatch) is reset to selection by the
    // backend, which returns the updated progression; render it instead of stranding the user.
    extraProps.onVisitBlocked = handleVisitBlocked;
  }
  if (currentStep === ProgressionStep.cms_consent) {
    // Declining ends the enrollment; there's no ACCESS landing page to route to yet, so closing
    // returns the user to wherever the enrollment was opened from.
    extraProps.onDecline = handleArchive;
  }
  if (currentStep === ProgressionStep.access_ineligible) {
    // Nothing follows an ineligible verdict, so the notice ends the enrollment.
    extraProps.onAcknowledge = handleArchive;
  }
  if (currentStep === ProgressionStep.async_visit) {
    extraProps.handleComplete = handleComplete;
    extraProps.clinicRequestId = clinicRequestId;
    extraProps.onClose = onClose;
    extraProps.onClinicRequestExpired = handleClinicRequestExpired;
  }
  if (currentStep === ProgressionStep.questionnaire) {
    extraProps.progressionId = progression.id;
    extraProps.onClose = onClose ?? (() => {});
    extraProps.isPending = isAdvancing;
    extraProps.onBackButtonChange = handleBackButtonChange;
  }

  const dialogShellProps = {
    dialogVariant: stepConfig.dialogVariant,
    variant: stepConfig.variant,
    dataTrackingIdPrefix: stepConfig.dataTrackingIdPrefix,
    onClose,
    hideCloseButton,
    showBackButton,
    onBackPress,
    backButtonDisabled: isGoingBack,
  };

  const stepContent = (
    <StepComponent onContinue={handleNextStep} {...extraProps} />
  );

  return (
    <UserProgressionContext.Provider value={{ progression }}>
      {stepConfig.handlesDialogShell ? (
        <StepComponent
          onContinue={handleNextStep}
          {...extraProps}
          {...dialogShellProps}
        />
      ) : (
        <UserProgressionScreenWrapper {...dialogShellProps}>
          {stepContent}
        </UserProgressionScreenWrapper>
      )}
    </UserProgressionContext.Provider>
  );
};

export default UserProgressionFlow;
